// Pure value helpers for rendering/editing custom user attribute values (#267).
// Shared by the admin user-edit modal (UsersPage) and the self-service
// profile modal — both render the same <UserAttributeFields> component but
// pass it a different definition list and editability rule (see isSelfEditable).

/** The empty/default value for a given attribute type. */
function emptyValueForType(type) {
  if (type === 'multiselect') return []
  if (type === 'boolean') return false
  return ''
}

/** Whether a value is "empty" for its type — i.e. equivalent to never having been set. */
function isEmptyValue(type, value) {
  if (type === 'multiselect') return !Array.isArray(value) || value.length === 0
  if (type === 'boolean') return value !== true
  return value === '' || value === undefined || value === null
}

function valuesEqual(type, a, b) {
  if (type === 'multiselect') {
    const arrA = Array.isArray(a) ? a : []
    const arrB = Array.isArray(b) ? b : []
    if (arrA.length !== arrB.length) return false
    const sortedA = [...arrA].sort()
    const sortedB = [...arrB].sort()
    return sortedA.every((v, i) => v === sortedB[i])
  }
  return a === b
}

/**
 * Seed a { [key]: value } map for a definition list from stored values,
 * defaulting anything absent to the type's empty value so form inputs are
 * always controlled.
 */
export function initialValuesFor(definitions, stored) {
  const source = stored || {}
  const values = {}
  for (const def of definitions) {
    const raw = source[def.key]
    if (def.type === 'multiselect') {
      values[def.key] = Array.isArray(raw) ? raw : []
    } else if (def.type === 'boolean') {
      values[def.key] = raw === true
    } else {
      values[def.key] = raw === undefined || raw === null ? '' : raw
    }
  }
  return values
}

/**
 * Diff `current` form values against `stored` values and return only the
 * keys that actually changed, ready to send as the `attributes` field of a
 * PUT /users/me or PUT /users/{userId} request. A value cleared back to its
 * type's empty default is sent as explicit `null` (removes the stored key);
 * an unchanged value is omitted entirely.
 */
export function changedValues(definitions, stored, current) {
  const storedSource = stored || {}
  const currentSource = current || {}
  const changes = {}

  for (const def of definitions) {
    const storedValue = initialValuesFor([def], storedSource)[def.key]
    const currentValue = currentSource[def.key]

    if (valuesEqual(def.type, storedValue, currentValue)) continue

    changes[def.key] = isEmptyValue(def.type, currentValue) ? null : currentValue
  }

  return changes
}

/** Only `editable` attributes may be set by the user themselves. */
export function isSelfEditable(definition) {
  return definition?.visibility === 'editable'
}

export { emptyValueForType }
